import { useMemo } from 'react';
import isEqual from 'lodash-es/isEqual';
import { getListOrderIdMap } from '../../../../hooks';
import { CalendarEntityType } from '../../../../stores';
import { ReorderConfirmHandlerResponse, useColumnEntityReorderStore } from '../../providers';
import { ColumnEntity } from '../../types';

export type UseAddNewOrderToQueueProps = {
  locationId: string;
  calendarEntityType: CalendarEntityType;
  setList: (list: ColumnEntity[]) => void;
  resetList: () => void;
  originalReorderedList: ColumnEntity[];
};

export const useAddNewOrderToQueue = ({
  locationId,
  calendarEntityType,
  setList,
  resetList,
  originalReorderedList,
}: UseAddNewOrderToQueueProps) => {
  const initialListOrderMap = useMemo(() => getListOrderIdMap(originalReorderedList), [originalReorderedList]);

  const { addToColumnReorderHandlers, removeColumnReorderHandler } = useColumnEntityReorderStore([
    'addToColumnReorderHandlers',
    'removeColumnReorderHandler',
  ]);

  //onConfirmHandlerCreator is used to create a confirm handler to be added to the queue
  const onConfirmHandlerCreator = (newOrderIdMap: Record<string, number>) => () => {
    // remove this handler from the queue
    removeColumnReorderHandler(locationId);

    // return the new order to the preference store
    return [calendarEntityType, locationId, newOrderIdMap] satisfies ReorderConfirmHandlerResponse;
  };

  const onCancelHandlerCreator = () => () => {
    resetList();
    // Clean up the confirm handler after cancelling
    removeColumnReorderHandler(locationId);
  };

  const addNewOrderToQueue = (newOrderIdMap: Record<string, number>, newList: ColumnEntity[]) => {
    setList(newList);
    if (isEqual(initialListOrderMap, newOrderIdMap)) {
      setList(newList);
      removeColumnReorderHandler(locationId);
    } else {
      // Register the confirm handler to be called later
      addToColumnReorderHandlers(locationId, {
        confirm: onConfirmHandlerCreator(newOrderIdMap),
        cancel: onCancelHandlerCreator(),
      });
    }
  };

  return { addNewOrderToQueue };
};
